import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, { getSocket, initSocket } from '../api';
import { useAuth } from './AuthContext';
import { useCurrentWeek } from './CurrentWeekContext';
import { getCurrentWeek } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import LockNotification from '../components/LockNotification';
import { formatLineVerificationMessage, getPickTypeDisplay } from '../utils/lineDataUtils';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

const GameContext = createContext();

// Utility function at module level
const isPickMismatched = (pick, mismatchedLines) => {
  return mismatchedLines.some(mismatch =>
    mismatch.gameId === pick.gameId &&
    mismatch.pickType === pick.pickType
  );
};

// Utility function at module level
const isNewLock = (newLock, existingLocks) => {
  return !existingLocks.some(existingLock =>
    existingLock.gameId === newLock.gameId &&
    existingLock.pickType === newLock.pickType
  );
};

const updateLockInCache = (lock, gameId, userId, won_lost, margin) => {
  if (lock.gameId === gameId && lock.userId === userId) {
    return {
      ...lock,
      won_lost,
      margin,
      status: 'completed'
    };
  }
  return lock;
};

// Utility function at module level
const updateCacheForWeek = (weekLocks, gameId, userId, won_lost, margin) => {
  return weekLocks.map(lock => updateLockInCache(lock, gameId, userId, won_lost, margin));
};

const updateEntireCache = (cache, data) => {
  const newCache = { ...cache };
  Object.keys(newCache).forEach(weekNumber => {
    newCache[weekNumber] = updateCacheForWeek(
      newCache[weekNumber],
      data.gameId,
      data.userId,
      data.won_lost,
      data.margin
    );
  });
  return newCache;
};

// Utility functions at module level
const isWatchMatchingLock = (watch, lock) =>
  lock.gameId === watch.gameId && lock.pickType === watch.pickType;

const filterUnlockedWatches = (watches, currentUserLocks) => {
  return watches.filter(watch =>
    !currentUserLocks.some(lock => isWatchMatchingLock(watch, lock))
  );
};

// Utility function at module level
const filterLocksFromCache = (cache, lockId) => {
  const newCache = { ...cache };
  Object.keys(newCache).forEach(weekNumber => {
    newCache[weekNumber] = newCache[weekNumber].filter(lock => lock.id !== lockId);
  });
  return newCache;
};

// At module level
const updateGameWithLiveData = (game, update) => {
  if (!update || !game) return game;
  return {
    ...game,
    game_status: update.game_status || game.game_status,
    period: update.period || game.period,
    clock: update.clock || game.clock,
    situation: update.situation || game.situation,
    possession: update.possession || game.possession,
    last_play: update.last_play || game.last_play,
    home_score: update.home_score ?? game.home_score,
    away_score: update.away_score ?? game.away_score,
    home_line_scores: update.home_line_scores || game.home_line_scores,
    away_line_scores: update.away_line_scores || game.away_line_scores,
    betting: {
      ...game.betting,
      spread: update.betting?.spread ?? game.betting?.spread,
      over_under: update.betting?.over_under ?? game.betting?.over_under
    }
  };
};

// Utility function for debounced updates
const debouncedGameUpdates = debounce((updates, setGames, setGameUpdates) => {
  if (!updates) return;

  setGames(prevGames => {
    const gameUpdatesChanges = {};
    const updatedGames = prevGames.map(game => {
      const update = updates.find(u => u.id === game.id);
      if (!update || !game) return game;

      gameUpdatesChanges[game.id] = {
        scores: update.home_score !== game.home_score || update.away_score !== game.away_score,
        clock: update.clock !== game.clock || update.period !== game.period,
        timestamp: Date.now()
      };

      return updateGameWithLiveData(game, update);
    });

    setGameUpdates(prev => ({ ...prev, ...gameUpdatesChanges }));
    return updatedGames;
  });
}, 1000); // 1 second debounce

export const GameProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [redirected, setRedirected] = useState(false);
  const { currentWeek, calendar, setCurrentWeek, isLoading: isCurrentWeekLoading } = useCurrentWeek();
  const [games, setGames] = useState([]);
  const [userPicks, setUserPicks] = useState([]);
  const [leaguePicks] = useState([]);
  const [watchedPicks, setWatchedPicks] = useState([]);
  const [watchLoading, setWatchLoading] = useState({});
  const [isLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPeeking, setIsPeeking] = useState(false);
  const [locksCache, setLocksCache] = useState({});
  const [adminLocks, setAdminLocks] = useState([]);
  const [notification, setNotification] = useState(null);
  const [activeUserCount, setActiveUserCount] = useState(0);
  const [verificationState, setVerificationState] = useState({
    isOpen: false,
    mismatchedLines: [],
    originalRequest: null
  });
  const [lineChanges] = useState({});
  const [animatingGames, setAnimatingGames] = useState({});
  const [userId, setUserId] = useState(null);
  const [byeWeekState, setByeWeekState] = useState({
    showOption: false,
    isPending: false,
    weekNumber: null,
    bye_used: false
  });
  const [gameUpdates, setGameUpdates] = useState({});
  const [userSeasonLocks, setUserSeasonLocks] = useState([]);

  useEffect(() => {
    if (user?.id) {
      setUserId(user.id);

      api.getByeStatus()
        .then(response => {
          const byeUsed = response.data.bye_used;
          setByeWeekState(prev => ({
            ...prev,
            bye_used: byeUsed,     // Store the bye_used status
            showOption: !byeUsed,  // Hide option if bye is used
            isPending: false
          }));
        })
        .catch(error => {
          console.error('Error setting bye week state:', error);
          setByeWeekState(prev => ({
            ...prev,
            bye_used: false,
            showOption: false,
            isPending: false
          }));
        });
    }
  }, [user]);

  const handleGameUpdate = useCallback((updatedGame) => {
    setGames(prevGames => prevGames.map(game =>
      game.id === updatedGame.id ? { ...game, ...updatedGame } : game
    ));
  }, []);

  const handlePickUpdate = useCallback((updatedPick) => {
    setUserPicks(prevPicks => {
      const existingPickIndex = prevPicks.findIndex(pick => pick.id === updatedPick.id);
      if (existingPickIndex !== -1) {
        return prevPicks.map(pick =>
          pick.id === updatedPick.id ? { ...pick, ...updatedPick } : pick
        );
      } else {
        return [...prevPicks, updatedPick];
      }
    });
  }, []);

  const handleToggleWatchPickResponse = useCallback((response) => {
    if (response.status === 'success') {
      setWatchedPicks(prevWatched => {
        const pickId = `${response.gameId}_${response.pickType}`;

        if (response.isWatched) {
          const existingPickIndex = prevWatched.findIndex(pick => pick.id === pickId);
          if (existingPickIndex !== -1) {
            return prevWatched.map((pick, index) =>
              index === existingPickIndex
                ? { ...pick, watchLine: response.watchLine, id: pickId }
                : pick
            );
          } else {
            const newPick = {
              id: pickId,
              gameId: response.gameId,
              pickType: response.pickType,
              watchLine: response.watchLine,
              liveLine: response.liveLine,
              weekNumber: response.weekNumber,
              status: 'watched',
              userId: userId
            };
            return [...prevWatched, newPick];
          }
        } else {
          return prevWatched.filter(pick => pick.id !== pickId);
        }
      });
    }
    setWatchLoading(prev => ({ ...prev, [`${response.gameId}_${response.pickType}`]: false }));
  }, [userId]);

  const handleLiveGameUpdates = useCallback((updates) => {
    debouncedGameUpdates(updates, setGames, setGameUpdates);
  }, []);

  const fetchLiveGameData = useCallback(() => {
    const socket = getSocket();
    if (socket) {
      socket.emit('live_game_updates', (updates) => {
        handleLiveGameUpdates(updates);
      });
    }
  }, [handleLiveGameUpdates]);

  const handlePendingStatusUpdate = useCallback((updatedPick) => {
    setWatchedPicks(prev => prev.map(pick =>
      pick.id === updatedPick.id ? { ...pick, status: updatedPick.status } : pick
    ));
  }, []);

  const handleAdminLocksFetched = useCallback((data) => {
    setAdminLocks(data.locks);
  }, []);


  const handlePurgeClientLock = useCallback((data) => {
    setLocksCache(prev => filterLocksFromCache(prev, data.lockId));
  }, []);

  const handlePurgeAllClientLocks = useCallback(() => {
    setLocksCache({});
  }, []);

  const fetchLocksForWeek = useCallback(async (week) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('request_locks', { week });
    }
  }, []);

  const handleLocksPurged = useCallback(() => {
    fetchLocksForWeek(currentWeek);
  }, [currentWeek, fetchLocksForWeek]);

  const currentWeekData = useMemo(() => {
    return calendar.find(weekData => weekData.week === currentWeek);
  }, [calendar, currentWeek]);

  const maxPicks = useMemo(() => {
    return currentWeekData?.max_picks || 1;
  }, [currentWeekData]);

    const handlePicksLockedError = useCallback((error) => {
    console.error('Failed to lock picks:', error);
    setWatchedPicks(prev => prev.map(pick =>
      pick.status === 'locking' ? { ...pick, status: 'pending' } : pick
    ));
  }, [setWatchedPicks]);

  const handleLocksData = useCallback((data) => {
    if (data && data.week && data.locks) {
      setLocksCache(prevCache => ({
        ...prevCache,
        [data.week]: data.locks
      }));
    } else {
      console.error('[handleLocksData] Received invalid locks data:', data);
    }
  }, []);

  const clearByeWeekState = useCallback(() => {
    setByeWeekState({
      showOption: false,
      isPending: false,
      weekNumber: null,
      bye_used: false
    });
    localStorage.removeItem('byeWeekState');
  }, []);


  // Handle watched picks cleanup after successful lock
  const handleWatchedPicksCleanup = useCallback((allConfirmedLocks) => {
    if (!user || !allConfirmedLocks.length) return;

    const currentUserLocks = allConfirmedLocks.filter(lock => lock.userId === user.id);

    setWatchedPicks(prev => {
      let updatedWatches = filterUnlockedWatches(prev, currentUserLocks);

      if (currentUserLocks.length >= maxPicks) {
        updatedWatches = updatedWatches.filter(watch => watch.userId !== user.id);
      }

      return updatedWatches;
    });

    // Check if user has used their bye week
    const hasByeLock = currentUserLocks.some(lock => lock.pickType === 'bye');
    if (hasByeLock) {
      clearByeWeekState();
    }
  }, [user, maxPicks, clearByeWeekState]);

  const handleVerifyLockLines = useCallback((data) => {
    const mismatchDetails = data.mismatchedLines.map(line => ({
      gameId: line.gameId,
      pickType: line.pickType,
      oldLine: line.lockLine,
      newLine: line.currentLine,
      home_team: line.home_team,
      away_team: line.away_team,
      home_team_abv: line.home_team_abv,
      away_team_abv: line.away_team_abv,
      displayMessage: formatLineVerificationMessage(
        {
          id: line.gameId,
          home_team: line.home_team,
          away_team: line.away_team,
          home_team_abv: line.home_team_abv,
          away_team_abv: line.away_team_abv
        },
        line.pickType,
        line.currentLine,
        line.lockLine
      )
    }));

    setVerificationState({
      isOpen: true,
      mismatchedLines: mismatchDetails,
      originalRequest: data.originalRequest
    });
  }, []);

    useEffect(() => {
    if (!user && !redirected) {
      setRedirected(true);
      navigate('/login', { replace: true });
    }
  }, [user, navigate, redirected]);


  const handleGameAnimation = useCallback(async (gameId, phase, isCurrentUser = false) => {
    // Don't animate if not current user's lock
    if (!isCurrentUser) {
      console.log('Animation skipped: not current user');
      return;
    }

    // Check if game already has locks
    const hasExistingLocks = Object.values(locksCache).some(weekLocks =>
      weekLocks.some(lock => lock.gameId === gameId)
    );

    // Don't animate if game already has locks
    if (hasExistingLocks) {
      console.log('Animation skipped: game already has locks');
      return;
    }

    if (phase === 'fadeOut') {
      setAnimatingGames(prev => ({ ...prev, [gameId]: 'fadeOut' }));
      await new Promise(resolve => setTimeout(resolve, 500));
    } else if (phase === 'fadeIn') {
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Find the specific game card
      const gameCard = document.querySelector(`[data-game-id="${gameId}"]`);
      if (gameCard) {
        // Calculate position to scroll to (slightly above the game card)
        const cardPosition = gameCard.getBoundingClientRect().top;
        const offset = 200; // Increased from 100 to 200
        const scrollPosition = cardPosition + window.pageYOffset - offset;

        // Smooth scroll to position
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });

        await new Promise(resolve => setTimeout(resolve, 500));
      }

      await new Promise(resolve => setTimeout(resolve, 750));
      setAnimatingGames(prev => ({ ...prev, [gameId]: 'fadeIn' }));
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }, [locksCache]);

  // Add new handler for pick completion
  const handlePickCompleted = useCallback((data) => {
    setLocksCache(prevCache => updateEntireCache(prevCache, data));
  }, []);

    useEffect(() => {
    if (user) {
      initSocket();
      const socket = getSocket();

      if (socket) {
        socket.on('error', (error) => {
          if (error.message?.includes('authentication') || error.message?.includes('unauthorized')) {
            navigate('/login', { replace: true });
          } else if (error.message?.includes('bye week')) {
            // Clear the bye week state
            setByeWeekState(prev => ({
              ...prev,
              showOption: false,
              isPending: false,
              bye_used: true
            }));
            // Show notification
            setNotification(`Error: ${error.message}`);
          }
          console.error('[socket:error]', error);
        });

        // Connection events
        socket.on('connect', () => {
        });

        socket.on('disconnect', () => {
        });

        // Game and Pick updates
        socket.on('game_update', handleGameUpdate);
        socket.on('pick_update', handlePickUpdate);
        socket.on('live_game_updates', handleLiveGameUpdates);

        // Watch-related events
        socket.on('toggle_watch_pick_response', handleToggleWatchPickResponse);
        socket.on('pending_status_update', handlePendingStatusUpdate);
        socket.on('all_watched_picks_update', (watchedPicksData) => {
          const watchedPicksWithIds = watchedPicksData.map(pick => ({
            ...pick,
            id: `${pick.gameId}_${pick.pickType}`
          }));
          setWatchedPicks(watchedPicksWithIds);
        });

        // Lock-related events
        socket.on('locks_data', handleLocksData);
        socket.on('picks_locked_success', async (data) => {
          if (data.confirmedLocks?.length > 0) {
            const weekNumber = data.confirmedLocks[0].weekNumber;
            const existingLocks = locksCache[weekNumber] || [];

            // Find newly added locks by comparing with existing locks
            const newLocks = data.confirmedLocks.filter(newLock =>
              isNewLock(newLock, existingLocks)
            );


            // Animate only new locks
            for (const lock of newLocks) {
              console.log('Calling animation with lock:', {
                gameId: lock.gameId,
                userId: lock.userId,
                currentUser: user?.id
              });
              await handleGameAnimation(lock.gameId, 'fadeOut', lock.userId === user?.id);
            }

            // Update cache with all confirmed locks to stay in sync
            handleLocksData({
              week: weekNumber,
              locks: data.confirmedLocks
            });
            handleWatchedPicksCleanup(data.confirmedLocks);

            // Fade in only the new locks
            for (const lock of newLocks) {
              console.log('Calling animation with lock:', {
                gameId: lock.gameId,
                userId: lock.userId,
                currentUser: user?.id
              });
              await handleGameAnimation(lock.gameId, 'fadeIn', lock.userId === user?.id);
            }
          }
        });
        socket.on('picks_locked_error', handlePicksLockedError);
        socket.on('pick_completed', handlePickCompleted);

        // Admin-related events
        socket.on('admin_locks_fetched', handleAdminLocksFetched);
        socket.on('locks_updated', handleLocksPurged);
        socket.on('purge_client_lock', handlePurgeClientLock);
        socket.on('purge_all_client_locks', handlePurgeAllClientLocks);

        socket.on('lock_notification', async (data) => {
          console.log('Calling animation with data:', {
            gameId: data.gameId,
            userId: data.userId,
            currentUser: user?.id
          });

          // Display notification and update UI
          const pickTypeDisplay = getPickTypeDisplay(data.pickType, data.homeTeamAbbr, data.awayTeamAbbr);

          const gameDisplay = `${data.awayTeamAbbr}@${data.homeTeamAbbr}`;

          let message;
          if (data.pickType === 'over' || data.pickType === 'under') {
            message = `${data.username} has locked in ${pickTypeDisplay}${Math.abs(data.lockLine)} for ${gameDisplay} Week ${data.weekNumber}`;
          } else {
            message = `${data.username} has locked in ${pickTypeDisplay} ${data.lockLine} for ${gameDisplay} Week ${data.weekNumber}`;
          }

          setNotification(message);
        });

        socket.on('verify_lock_lines', handleVerifyLockLines);

        return () => {
          // Connection cleanup
          socket.off('connect');
          socket.off('disconnect');
          socket.off('error');

          // Game and Pick cleanup
          socket.off('game_update', handleGameUpdate);
          socket.off('pick_update', handlePickUpdate);
          socket.off('live_game_updates', handleLiveGameUpdates);

          // Watch-related cleanup
          socket.off('toggle_watch_pick_response', handleToggleWatchPickResponse);
          socket.off('pending_status_update', handlePendingStatusUpdate);
          socket.off('all_watched_picks_update');

          // Lock-related cleanup
          socket.off('locks_data', handleLocksData);
          socket.off('picks_locked_success');
          socket.off('picks_locked_error', handlePicksLockedError);
          socket.off('pick_completed', handlePickCompleted);


          // Admin cleanup
          socket.off('admin_locks_fetched', handleAdminLocksFetched);
          socket.off('locks_updated', handleLocksPurged);
          socket.off('purge_client_lock', handlePurgeClientLock);
          socket.off('purge_all_client_locks', handlePurgeAllClientLocks);

          socket.off('lock_notification');
          socket.off('verify_lock_lines', handleVerifyLockLines);
        };
      }
    }
  }, [
    user,
    handleGameUpdate,
    handlePickUpdate,
    handleToggleWatchPickResponse,
    handleLiveGameUpdates,
    handlePendingStatusUpdate,
    handleAdminLocksFetched,
    handleLocksPurged,
    handlePurgeClientLock,
    handlePurgeAllClientLocks,
    handlePicksLockedError,
    handleLocksData,
    handleWatchedPicksCleanup,
    setWatchedPicks,
    navigate,
    handleVerifyLockLines,
    handlePickCompleted,
    handleGameAnimation,
    locksCache
  ]);

  const toggleWatchPick = useCallback((gameId, pickType, weekNumber, watchLine, liveLine, isBetSlipOpen) => {
    const socket = getSocket();
    if (socket) {
      const pickId = `${gameId}_${pickType}`;
      socket.emit('toggle_watch_pick', { gameId, pickType, weekNumber, watchLine, liveLine });
      setWatchLoading(prev => ({ ...prev, [pickId]: true }));

      setWatchedPicks(prev => {
        const existingPickIndex = prev.findIndex(pick => pick.id === pickId);

        if (existingPickIndex !== -1) {
          // If the pick exists, remove it (unwatch)
          return prev.filter(pick => pick.id !== pickId);
        } else {
          // If the pick doesn't exist, add it (watch)
          return [...prev, { id: pickId, gameId, pickType, watchLine, liveLine, userId: user?.id }];
        }
      });
      // Only trigger peek effect if BetSlip is not already open
      if (!isBetSlipOpen) {
        setIsPeeking(true);
        setTimeout(() => setIsPeeking(false), 3000);
      }
    }
  }, [user?.id]);

  const fetchGames = useCallback(async (week) => {
    if (week === null || week === undefined) {
      console.error('Attempted to fetch games with null or undefined week');
      return;
    }
    try {
      const response = await api.get(`/games?week=${week}`);
      setGames(response.data);
    } catch (error) {
      console.error('Error fetching games:', error);
      setError('Failed to fetch games');
    }
  }, []);

  const fetchWatchedPicks = useCallback(() => {
    if (!user?.id) return;

    const socket = getSocket();
    if (socket) {
      socket.emit('fetch_all_watched_picks');
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isCurrentWeekLoading && currentWeek !== null && user?.id) {
      fetchGames(currentWeek);
      fetchWatchedPicks();
    }
  }, [currentWeek, isCurrentWeekLoading, user?.id, fetchGames, fetchWatchedPicks]);

  const togglePendingStatus = useCallback((pickId) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('toggle_pending_status', { pickId });
      // Optimistically update the watchedPicks
      setWatchedPicks(prev => prev.map(pick => {
        if (pick.id === pickId) {
          const newStatus = pick.status === 'pending' ? 'watched' : 'pending';
          return { ...pick, status: newStatus };
        }
        return pick;
      }));
    }
  }, []);

  const getLocksForWeek = useCallback((weekNumber) => {
    return locksCache[weekNumber] || [];
  }, [locksCache]);

  const changeWeek = useCallback(async (newWeek) => {
    setCurrentWeek(newWeek);
    if (!locksCache[newWeek]) {
      await fetchLocksForWeek(newWeek);
    }
  }, [fetchLocksForWeek, locksCache, setCurrentWeek]);

  // Use getCurrentWeek from dateUtils to determine the current week
  useEffect(() => {
    if (calendar.length > 0 && !isCurrentWeekLoading) {
      const calculatedCurrentWeek = getCurrentWeek(calendar);
      setCurrentWeek(calculatedCurrentWeek);
    }
  }, [calendar, setCurrentWeek, isCurrentWeekLoading]);

  const lockPicks = useCallback((pendingPicks, weekNumber) => {
    const socket = getSocket();
    if (socket && user) {
      const locksToSend = pendingPicks.map(pick => {
        // If it's a bye pick, return simplified lock data
        if (pick.pickType === 'bye') {
          return {
            userId: user.id,
            gameId: null,
            pickType: 'bye',
            weekNumber: weekNumber,
            lockLine: 0,
            teamId: null,
            pickLeadTime: null
          };
        }

        // Otherwise, process regular pick data
        const game = games.find(g => g.id === pick.gameId);
        const pickLeadTime = Math.floor((new Date(game.start_date) - new Date()) / 1000);
        return {
          userId: user.id,
          gameId: pick.gameId,
          pickType: pick.pickType,
          weekNumber: weekNumber,
          lockLine: pick.lockLine,
          teamId: pick.pickType === 'home' ? game.home_team.id : game.away_team.id,
          pickLeadTime: pickLeadTime
        };
      });

      // Emit the lock request to the server
      socket.emit('lock_picks', locksToSend);
    }
  }, [games, user]);

  const fetchAdminLocks = useCallback((source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_fetch_locks', { source });
    }
  }, []);

  const purgeAdminLock = useCallback((lockId, source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_purge_lock', { lockId, source });
    }
  }, []);

  const purgeAllAdminLocks = useCallback((source) => {
    const socket = getSocket();
    if (socket) {
      socket.emit('admin_purge_all_locks', { source });
    }
  }, []);

  const fetchActiveUserCount = useCallback(async () => {
    try {
        const response = await api.getActiveUserCount();
        setActiveUserCount(response.data.count);
    } catch (error) {
        console.error('Error fetching active user count:', error);
        setActiveUserCount(0);
    }
  }, []);

  useEffect(() => {
    if (user && currentWeek) {
      fetchActiveUserCount();
    }
  }, [fetchActiveUserCount, currentWeek, user]);

  const handleVerificationResponse = useCallback((confirmed) => {
    const socket = getSocket();
    if (!socket) return;

    if (!confirmed) {
      setWatchedPicks(prev => prev.map(pick =>
        isPickMismatched(pick, verificationState.mismatchedLines)
          ? { ...pick, status: 'watched' }
          : pick
      ));
    }

    socket.emit('confirm_lock_lines', {
      confirmed,
      originalRequest: verificationState.originalRequest,
      mismatchedLines: verificationState.mismatchedLines
    });

    setVerificationState(prev => ({ ...prev, isOpen: false }));
  }, [verificationState]);

  const handlePickLocked = useCallback(async (lockData) => {
    try {
      if (lockData.pickType === 'bye') {
        // Clear bye week state immediately before other updates
        setByeWeekState({
          showOption: false,
          isPending: false,
          weekNumber: null,
          bye_used: true
        });

        // Update user's bye_used status
        setUser(prev => ({
          ...prev,
          bye_used: true
        }));

        // Update locks cache
        setLocksCache(prev => {
          const weekKey = `week_${lockData.weekNumber}`;
          const existingWeekLocks = prev[weekKey] || [];
          return {
            ...prev,
            [weekKey]: [...existingWeekLocks, { ...lockData, userId: user.id }]
          };
        });
      }
    } catch (error) {
      console.error('Error handling pick lock:', error);
    }
  }, [user?.id, setUser]);

  const toggleByeWeekOption = useCallback((show) => {
    setByeWeekState(prev => {
      const newState = {
        ...prev,
        showOption: show,
        // Clear pending status when hiding the option
        isPending: show ? prev.isPending : false,
        weekNumber: show ? prev.weekNumber : null
      };
      localStorage.setItem('byeWeekState', JSON.stringify(newState));
      return newState;
    });
  }, []);

  const toggleByeWeekPending = useCallback((weekNumber) => {
    setByeWeekState(prev => {
      const newState = {
        ...prev,
        isPending: !prev.isPending,
        weekNumber: !prev.isPending ? weekNumber : null
      };
      localStorage.setItem('byeWeekState', JSON.stringify(newState));
      return newState;
    });
  }, []);

  // Add this new function to get max picks for any week
  const getMaxPicksForWeek = useCallback((weekNumber) => {
    const weekData = calendar.find(weekData => weekData.week === weekNumber);
    return weekData?.max_picks || 1;
  }, [calendar]);

  const fetchUserSeasonLocks = useCallback(async () => {
    try {
      const response = await api.fetchUserSeasonLocks();
      setUserSeasonLocks(response.data.seasonLocks);
    } catch (error) {
      console.error('Error fetching season locks:', error);
    }
  }, []);

  // Add effect to fetch season locks when user changes
  useEffect(() => {
    if (user) {
      fetchUserSeasonLocks();
    }
  }, [user, fetchUserSeasonLocks]);

  const contextValue = useMemo(() => ({
    games,
    userPicks,
    leaguePicks,
    watchedPicks,
    setWatchedPicks,
    isLoading,
    error,
    currentWeek,
    watchLoading,
    gameUpdates,
    fetchGames,
    fetchWatchedPicks,
    toggleWatchPick,
    handleGameUpdate,
    handlePickUpdate,
    fetchLiveGameData,
    handleLiveGameUpdates,
    togglePendingStatus,
    isPeeking,
    lockPicks,
    locksCache,
    setLocksCache,
    maxPicks,
    getLocksForWeek,
    changeWeek,
    calendar,
    isCurrentWeekLoading,
    adminLocks,
    fetchAdminLocks,
    purgeAdminLock,
    purgeAllAdminLocks,
    fetchLocksForWeek,
    user,
    activeUserCount,
    fetchActiveUserCount,
    verificationState,
    handleVerificationResponse,
    lineChanges,
    handlePickCompleted,
    animatingGames,
    handleGameAnimation,
    handlePickLocked,
    byeWeekState,
    toggleByeWeekOption,
    toggleByeWeekPending,
    getMaxPicksForWeek,
    clearByeWeekState,
    userSeasonLocks,
    fetchUserSeasonLocks
  }), [
    games,
    userPicks,
    leaguePicks,
    watchedPicks,
    setWatchedPicks,
    isLoading,
    error,
    currentWeek,
    watchLoading,
    gameUpdates,
    fetchGames,
    fetchWatchedPicks,
    toggleWatchPick,
    handleGameUpdate,
    handlePickUpdate,
    fetchLiveGameData,
    handleLiveGameUpdates,
    togglePendingStatus,
    isPeeking,
    lockPicks,
    locksCache,
    setLocksCache,
    maxPicks,
    getLocksForWeek,
    changeWeek,
    calendar,
    isCurrentWeekLoading,
    adminLocks,
    fetchAdminLocks,
    purgeAdminLock,
    purgeAllAdminLocks,
    fetchLocksForWeek,
    user,
    activeUserCount,
    fetchActiveUserCount,
    verificationState,
    handleVerificationResponse,
    lineChanges,
    handlePickCompleted,
    animatingGames,
    handleGameAnimation,
    handlePickLocked,
    byeWeekState,
    toggleByeWeekOption,
    toggleByeWeekPending,
    getMaxPicksForWeek,
    clearByeWeekState,
    userSeasonLocks,
    fetchUserSeasonLocks
  ]);

  return (
    <GameContext.Provider value={contextValue}>
      {children}
      {notification && (
        <LockNotification
          message={notification}
          onClose={() => setNotification(null)}
        />
      )}
    </GameContext.Provider>
  );
};

GameProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};

export default GameContext;
